/**
 * The kit. Small on purpose — this console is tables, and a table needs six components.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useEffect } from "react";

export const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(" ");

// ── surfaces ─────────────────────────────────────────────────────────────────

export function Card({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cx("rounded-lg border border-line bg-surface", className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </header>
      )}
      <div className={bodyClassName ?? "p-4"}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-bold text-ink">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-ink-soft">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

// ── button ───────────────────────────────────────────────────────────────────

type Variant = "primary" | "default" | "ghost" | "danger" | "good";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-accent text-white border-accent hover:opacity-90",
  default: "bg-surface text-ink border-line hover:bg-line-soft",
  ghost: "bg-transparent text-ink-soft border-transparent hover:bg-line-soft",
  danger: "bg-bad text-white border-bad hover:opacity-90",
  good: "bg-good text-white border-good hover:opacity-90",
};

export function Button({
  variant = "default",
  size = "md",
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md" }) {
  return (
    <button
      type="button"
      {...rest}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 rounded-md border font-medium transition",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
        VARIANTS[variant],
        className,
      )}
    />
  );
}

// ── badge ────────────────────────────────────────────────────────────────────

export type Tone = "neutral" | "info" | "active" | "good" | "warn" | "bad";

const TONES: Record<Tone, string> = {
  neutral: "bg-line-soft text-ink-soft border-line",
  info: "bg-accent-soft text-accent border-accent/25",
  active: "bg-accent text-white border-accent",
  good: "bg-good-soft text-good border-good/25",
  warn: "bg-warn-soft text-warn border-warn/30",
  bad: "bg-bad-soft text-bad border-bad/25",
};

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium whitespace-nowrap",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Money, ids, counts, dates. Tabular figures + LTR isolation. See styles.css. */
export function Num({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx("num", className)}>{children}</span>;
}

// ── form fields ──────────────────────────────────────────────────────────────

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cx("block", className)}>
      <span className="mb-1 block text-xs font-medium text-ink-soft">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-ink-faint">{hint}</span> : null}
    </label>
  );
}

const CONTROL =
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-ink " +
  "placeholder:text-ink-faint focus:border-accent focus:outline-none disabled:opacity-50";

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx(CONTROL, className)} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cx(CONTROL, "min-h-20", className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx(CONTROL, className)}>
      {children}
    </select>
  );
}

// ── table ────────────────────────────────────────────────────────────────────

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="scroll-x">
      <table className={cx("w-full min-w-max text-right text-sm", className)}>{children}</table>
    </div>
  );
}

export function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={cx(
        "border-b border-line px-3 py-2 text-xs font-semibold whitespace-nowrap text-ink-soft",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cx("border-b border-line-soft px-3 py-2 align-middle", className)}>{children}</td>;
}

export function Tr({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={cx(onClick && "cursor-pointer hover:bg-line-soft", className)}
    >
      {children}
    </tr>
  );
}

// ── states ───────────────────────────────────────────────────────────────────

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-3 py-10 text-center text-sm text-ink-faint">{children}</p>;
}

export function Loading({ label = "جارٍ التحميل…" }: { label?: string }) {
  return <p className="px-3 py-10 text-center text-sm text-ink-faint">{label}</p>;
}

/** Errors are shown, never swallowed. The API's Arabic message is used verbatim. */
export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : "حدث خطأ غير متوقع.";
  return (
    <p
      className={cx(
        "rounded-md border border-bad/25 bg-bad-soft px-3 py-2 text-sm text-bad",
        className,
      )}
      role="alert"
    >
      {msg}
    </p>
  );
}

/** A fact the office must not misread as a problem. SYNC.md Rule 3. */
export function Note({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cx("rounded-md border border-line bg-line-soft px-3 py-2 text-xs text-ink-soft", className)}>
      {children}
    </p>
  );
}

// ── modal ────────────────────────────────────────────────────────────────────

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/30 p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx("w-full rounded-lg border border-line bg-surface shadow-lg", wide ? "max-w-3xl" : "max-w-md")}
      >
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="إغلاق">
            ✕
          </Button>
        </header>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// ── stat ─────────────────────────────────────────────────────────────────────

export function Stat({
  label,
  value,
  hint,
  tone = "neutral",
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  onClick?: () => void;
}) {
  const accents: Record<Tone, string> = {
    neutral: "text-ink",
    info: "text-accent",
    active: "text-accent",
    good: "text-good",
    warn: "text-warn",
    bad: "text-bad",
  };
  return (
    <div
      onClick={onClick}
      className={cx(
        "rounded-lg border border-line bg-surface p-4",
        onClick && "cursor-pointer hover:border-accent/40",
      )}
    >
      <p className="text-xs font-medium text-ink-soft">{label}</p>
      <p className={cx("mt-1.5 text-2xl font-bold", accents[tone])}>{value}</p>
      {hint ? <p className="mt-1 text-xs text-ink-faint">{hint}</p> : null}
    </div>
  );
}
